import React, { useMemo } from 'react';
import { useContactModal } from '../context/ContactModalContext';
import useSeo from '../hooks/useSeo';
import '../styles/pages/PricingStudio.css';

const INCLUDED = [
  {
    name: 'Discovery and scope',
    detail: 'We work out what the product needs to do, then write it down as a scope you can hold us to.'
  },
  {
    name: 'Product design',
    detail: 'Screens and flows designed for the people who will actually use them, not just a pretty deck.'
  },
  {
    name: 'Development',
    detail: 'Web, iOS, and Android built by the same team, so the pieces fit together.'
  },
  {
    name: 'Internal beta every two weeks',
    detail: 'You get a working build you can open and use. No month-long silences.'
  },
  {
    name: 'Launch',
    detail: 'Deployment, app store submissions, monitoring, and the paperwork that comes with going live.'
  },
  {
    name: 'Handover',
    detail: 'Code, accounts, and documentation are yours. No lock-in and no hostage situations.'
  }
];

const DRIVERS = [
  {
    name: 'How many platforms',
    detail: 'A web app alone costs less than web plus iOS plus Android. We can stage them instead of building everything at once.'
  },
  {
    name: 'Integrations',
    detail: 'Payments, KYC, ERPs, logistics providers, and legacy systems each add work, especially when their documentation is thin.'
  },
  {
    name: 'Compliance',
    detail: 'Regulated areas such as finance and health need audit trails, data controls, and review cycles that take real time.'
  },
  {
    name: 'Scale from day one',
    detail: 'Building for a thousand users is different from building for a million. We only build what you need now.'
  }
];

const FAQ = [
  {
    q: 'Why is there no per-user pricing?',
    a: 'We are not selling you a seat in someone else\u2019s product. We build software that you own, so pricing follows the size of the build, not the number of people who log in.'
  },
  {
    q: 'What does the first version actually include?',
    a: 'A real, working product: the core flows scoped with you, designed, built, tested, and launched on the platforms we agreed. It is not a prototype or a clickable mockup.'
  },
  {
    q: 'Can we start smaller than four months?',
    a: 'Sometimes. If the scope is narrow, for example a single web app with no heavy integrations, we can plan a shorter build. Tell us what you have in mind and we will be honest about whether it fits.'
  },
  {
    q: 'How do payments work?',
    a: 'A deposit reserves your place in the schedule and covers discovery and planning. The rest is split across milestones as each stage is delivered. Everything is written into the proposal before we start.'
  },
  {
    q: 'Who owns the code?',
    a: 'You do, on full payment. That includes the custom code, designs, and assets built for your project, along with the accounts and infrastructure they run on.'
  },
  {
    q: 'What happens after launch?',
    a: 'Defects in the agreed scope are fixed under warranty. Beyond that, most clients move to an ongoing arrangement for support, improvements, and the next set of features.'
  },
  {
    q: 'Do you take equity instead of fees?',
    a: 'Not usually. We are a studio, not a fund. If there is a genuinely good reason to structure things differently, we are happy to talk it through.'
  },
  {
    q: 'What are the extra costs?',
    a: 'Third-party costs such as hosting, domains, app store fees, paid APIs, and commercial licences sit outside our fees. We tell you what to expect during scoping so there are no surprises.'
  }
];

function Pricing() {
  const { openContactModal } = useContactModal();

  const seoConfig = useMemo(() => {
    const title = 'Pricing | BloomNav Technology Studio';
    const description =
      'Project-based pricing for custom software. A first version in 4 months from $16,000, plus ongoing engagements for teams that keep shipping.';
    return {
      title,
      description,
      canonicalPath: '/pricing',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      jsonLd: [
        {
          id: 'schema-pricing-offers',
          data: {
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            name: 'BloomNav',
            url: `${window.location.origin}/pricing`,
            description,
            areaServed: 'Worldwide',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Goa',
              addressCountry: 'IN'
            },
            makesOffer: [
              {
                '@type': 'Offer',
                name: "Let's begin",
                description:
                  'A first version in about 4 months: scoped project, execution plan, bi-weekly betas, launch on web, iOS, and Android, with code handover.',
                price: '16000',
                priceCurrency: 'USD',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  price: '16000',
                  priceCurrency: 'USD',
                  valueAddedTaxIncluded: false
                }
              },
              {
                '@type': 'Offer',
                name: "Let's continue",
                description:
                  'Ongoing engagement for live products: dedicated team, integrations, support, shared roadmap, and maintenance.'
              }
            ]
          }
        },
        {
          id: 'schema-pricing-faq',
          data: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a }
            }))
          }
        }
      ]
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="ps-page">
      <section className="ps-section ps-section--head">
        <span className="ps-kicker">Pricing</span>
        <h1 className="ps-title">
          Priced by the <em>project.</em>
        </h1>
        <p className="ps-lede">
          No seats, no tiers, no feature gates. We scope the work, quote a price, and build the
          thing. You own what we deliver.
        </p>
      </section>

      <section className="ps-section">
        <div className="ps-plans">
          <article className="ps-plan">
            <h3>Let&rsquo;s begin</h3>
            <p className="ps-plan-for">For a first version you can put in front of real users.</p>
            <div className="ps-plan-price">
              <span className="ps-plan-from">from</span>
              <span className="ps-plan-amount">$16,000</span>
              <span className="ps-plan-term">/ 4 months</span>
            </div>
            <ul className="ps-plan-list">
              <li>A detailed scoped project</li>
              <li>Execution plan with clear milestones</li>
              <li>Internal beta every two weeks</li>
              <li>Launch on web, iOS, and Android</li>
              <li>Code and accounts handed over to you</li>
              <li>Warranty on the delivered scope</li>
            </ul>
            <button
              type="button"
              className="ps-btn ps-btn--primary"
              onClick={() => openContactModal('demo')}
            >
              Book a call
            </button>
          </article>

          <article className="ps-plan ps-plan--dark">
            <h3>Let&rsquo;s continue</h3>
            <p className="ps-plan-for">For teams with a product live and a roadmap to work through.</p>
            <div className="ps-plan-price">
              <span className="ps-plan-amount">Custom</span>
              <span className="ps-plan-term">/ ongoing</span>
            </div>
            <ul className="ps-plan-list">
              <li>A dedicated team on your product</li>
              <li>Integrations with tools you use</li>
              <li>Support with real response times</li>
              <li>A roadmap we plan together</li>
              <li>Releases on a predictable cadence</li>
              <li>Monitoring and maintenance included</li>
            </ul>
            <button
              type="button"
              className="ps-btn ps-btn--paper"
              onClick={() => openContactModal('consultation')}
            >
              Talk to us
            </button>
          </article>
        </div>
      </section>

      <section className="ps-section">
        <header className="ps-section-head">
          <h2>
            What&rsquo;s <em>included.</em>
          </h2>
          <p>
            Every project runs the same way, whether it is a lending app or an internal operations
            system.
          </p>
        </header>
        <div className="ps-included">
          {INCLUDED.map((item) => (
            <div className="ps-included-item" key={item.name}>
              <h3>{item.name}</h3>
              <p>{item.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ps-section ps-section--dark">
        <header className="ps-section-head">
          <h2>
            What moves <em>the price.</em>
          </h2>
          <p>
            The starting figure covers a focused first version. These are the things that push a
            quote above it.
          </p>
        </header>
        <div className="ps-drivers">
          {DRIVERS.map((driver) => (
            <div className="ps-driver" key={driver.name}>
              <h3>{driver.name}</h3>
              <p>{driver.detail}</p>
            </div>
          ))}
        </div>
        <div className="ps-note">
          <span className="ps-note-dot" aria-hidden="true" />
          <p>
            <strong>You get the number before we start.</strong> After a scoping conversation we send
            a written scope and a fixed price. If the scope changes later, we quote the change first.
          </p>
        </div>
      </section>

      <section className="ps-section">
        <header className="ps-section-head">
          <h2>
            Common <em>questions.</em>
          </h2>
        </header>
        <div className="ps-faq">
          {FAQ.map((item) => (
            <details className="ps-faq-item" key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="ps-section">
        <div className="ps-cta">
          <div>
            <h2>Tell us what you want to build.</h2>
            <p>
              A short call is usually enough for us to say whether we are a fit and roughly what it
              will cost.
            </p>
          </div>
          <button
            type="button"
            className="ps-btn ps-btn--primary ps-btn--inline"
            onClick={() => openContactModal('demo')}
          >
            Book a call
          </button>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
